import type { FilterCondition } from './type.ts'

export type RangeFilterValue = { from: string; to: string }

/**
 * Пустое значение условия под выбранный `compare_type`.
 * `RANGE` хранит границы объектом `{ from, to }`; остальные условия — скаляром.
 * Без этого шаблон `v-model="filter.value.from"` падает TypeError на строке `''`.
 */
export const emptyValueForCompareType = (compareType: string | null): unknown => {
  if (compareType === 'RANGE') return { from: '', to: '' } satisfies RangeFilterValue
  return ''
}

/** Значение уже в форме диапазона (не массив и не null). */
export const isRangeFilterValue = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Подгоняет `value` под новый `compare_type` при смене условия.
 * При входе в `RANGE` из скаляра создаёт пустой `{from,to}` (значение «35»
 * не переносится в from). При уходе с `RANGE` объект заменяется пустой строкой,
 * иначе в текстовом поле окажется `[object Object]`.
 * Если форма уже подходит — возвращает `current` без изменений (пресеты).
 */
export const reshapeFilterValueForCompareType = (
  compareType: string | null,
  current: unknown,
): unknown => {
  if (compareType === 'RANGE') {
    return isRangeFilterValue(current) ? current : emptyValueForCompareType('RANGE')
  }
  if (isRangeFilterValue(current)) return emptyValueForCompareType(compareType)
  if (current === null || current === undefined) return emptyValueForCompareType(compareType)
  return current
}

/**
 * Проверяет, есть ли в значении условия реальные данные для фильтрации.
 * Для составных значений (`RANGE`: `{ from, to }`) достаточно, чтобы была заполнена
 * хотя бы одна граница; для списков — хотя бы один элемент.
 */
const isConditionValueFilled = (value: unknown): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.values(value).some(isConditionValueFilled)
  return true
}

/**
 * Проверяет, что условие фильтра заполнено полностью: выбрано поле, выбрано
 * реальное условие сравнения (не «Без условий», у которого `compare_type === null`)
 * и указано значение. Недозаполненное условие search-сервис не отклоняет как ошибку,
 * а молча пропускает — считает совпадающим для любой записи, поэтому такие условия
 * нельзя пускать в запрос.
 */
export const isFilterConditionComplete = (condition: FilterCondition): boolean =>
  Boolean(condition.field_id) &&
  Boolean(condition.compare_type) &&
  isConditionValueFilled(condition.value)

/**
 * Убирает из фильтра недозаполненные условия перед отправкой запроса на поиск.
 * Группы (OR), которые после чистки остались пустыми, удаляются целиком.
 */
export const stripIncompleteConditions = (filter: FilterCondition[][]): FilterCondition[][] =>
  filter
    .map((group) => group.filter(isFilterConditionComplete))
    .filter((group) => group.length > 0)

/**
 * Сравнивает границы диапазона: числа/годы — по числу, даты (ISO) — лексикографически.
 * Возвращает отрицательное, если from < to; 0 при равенстве; положительное, если from > to.
 */
const compareRangeBounds = (from: string, to: string): number => {
  const fromNum = Number(from)
  const toNum = Number(to)
  if (Number.isFinite(fromNum) && Number.isFinite(toNum)) {
    return fromNum - toNum
  }
  return from < to ? -1 : from > to ? 1 : 0
}

/** Ошибка валидации RANGE: незаполнена одна граница или «от» ≥ «до». */
export type RangeValidationError = 'partial' | 'unordered'

/**
 * Возвращает тип ошибки RANGE или null, если значение валидно / обе границы пусты
 * (пустое условие отсекается как недозаполненное отдельно).
 */
export const getRangeValidationError = (value: unknown): RangeValidationError | null => {
  if (!isRangeFilterValue(value)) return null
  const from = String(value.from ?? '').trim()
  const to = String(value.to ?? '').trim()
  if (!from && !to) return null
  if (!from || !to) return 'partial'
  if (compareRangeBounds(from, to) >= 0) return 'unordered'
  return null
}

/**
 * Первая ошибка RANGE среди условий фильтра, либо null.
 */
export const findRangeValidationError = (
  filter: FilterCondition[][],
): RangeValidationError | null => {
  for (const condition of filter.flat()) {
    if (condition.compare_type !== 'RANGE') continue
    const error = getRangeValidationError(condition.value)
    if (error) return error
  }
  return null
}

export const RANGE_VALIDATION_MESSAGES: Record<RangeValidationError, string> = {
  partial: 'необходимо заполнить значения "от" и "до"',
  unordered: 'значение "от" должно быть меньше "до"',
}
